import {
  Diamond,
  GitBranch,
  type LucideIcon,
  SquareTerminal,
  Timer,
  Workflow,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';
import { formatPreciseDuration } from '~/utils/date';

import type { HistorySpan, HistoryTimeline, SpanKind, SpanStatus } from '../../utils/history-spans';

const TICKS = 8;

/**
 * Per-kind visual identity (Temporal's Event History colour coding): each
 * workflow step type has its own icon and base colour; a span's status then
 * overrides the colour where it matters (failed = red, running = pulsing,
 * timed out = amber, canceled = muted).
 */
const KIND_STYLE: Record<SpanKind, { icon: LucideIcon; bar: string; text: string }> = {
  workflow: { icon: Workflow, bar: 'bg-success/70', text: 'text-success' },
  activity: { icon: Diamond, bar: 'bg-success/60', text: 'text-success' },
  timer: { icon: Timer, bar: 'bg-warning/80', text: 'text-warning' },
  childWorkflow: { icon: GitBranch, bar: 'bg-primary/70', text: 'text-primary' },
  marker: { icon: SquareTerminal, bar: 'bg-muted-foreground/40', text: 'text-foreground' },
  signal: { icon: Zap, bar: 'bg-info/70', text: 'text-info' },
};

/** Status overrides on top of the kind colour (completed keeps the kind's own). */
const STATUS_BAR: Partial<Record<SpanStatus, string>> = {
  running: 'bg-info/60 animate-pulse motion-reduce:animate-none',
  failed: 'bg-error/80',
  timedOut: 'bg-warning/80',
  canceled: 'bg-muted-foreground/40',
};
const STATUS_NODE: Record<SpanStatus, string> = {
  running: 'border-info bg-info-50 text-info',
  completed: 'border-success bg-success-50 text-success',
  failed: 'border-error bg-error-50 text-error',
  timedOut: 'border-warning bg-warning-50 text-warning',
  canceled: 'border-muted-foreground/50 bg-muted text-muted-foreground',
};

/** Bar colour for a span: status override (if any), else its kind's own colour. */
function barTone(span: HistorySpan): string {
  // Timers keep amber while running/complete — "waiting" is what a timer does.
  if (span.kind === 'timer' && (span.status === 'completed' || span.status === 'running')) {
    return cn(
      KIND_STYLE.timer.bar,
      span.status === 'running' && 'animate-pulse motion-reduce:animate-none',
    );
  }
  if (span.status === 'completed') return KIND_STYLE[span.kind].bar;
  return STATUS_BAR[span.status] ?? KIND_STYLE[span.kind].bar;
}

/** Node colour for a span's closing node (timers close amber, not green). */
function endNodeTone(span: HistorySpan): string {
  if (span.kind === 'timer' && span.status === 'completed')
    return 'border-warning bg-warning-50 text-warning';
  return STATUS_NODE[span.status];
}

interface Props {
  timeline: HistoryTimeline;
  /** Opens the drawer on a span's first event. */
  onSelectEvent: (eventId: number) => void;
}

/**
 * Gantt-style lanes for one execution, styled after Temporal's Event History:
 * a colour + icon per step kind (workflow, activity, timer, child workflow,
 * marker, signal), icon nodes at each bar's start (scheduled) and end
 * (status-coloured), vertical tick gridlines and a legend — all on a shared ms
 * axis from the first event. Open spans run to the latest event. Clicking a
 * lane opens its first event in the detail drawer.
 */
export function EventTimeline({ timeline, onSelectEvent }: Props) {
  const { t } = useTranslation('workflow-inspection');
  const { workflow, spans, totalMs } = timeline;
  if (!workflow) return null;
  // Keep a non-zero scale so a single-event history still renders.
  const scale = Math.max(totalMs, 1);
  const pct = (ms: number) => Math.min(100, (ms / scale) * 100);
  // Only list kinds that actually appear, so the legend explains this run.
  const kindsPresent = (
    ['workflow', 'activity', 'timer', 'childWorkflow', 'marker', 'signal'] as SpanKind[]
  ).filter((k) => k === 'workflow' || spans.some((s) => s.kind === k));

  return (
    <div
      className="bg-background rounded-lg border"
      aria-label={t('history.timeline.ariaLabel')}
      data-testid="workflow-inspector-timeline"
    >
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-3 py-2 text-xs">
        {kindsPresent.map((kind) => {
          const { icon: Icon, text } = KIND_STYLE[kind];
          return (
            <li key={kind} className="text-muted-foreground flex items-center gap-1.5">
              <Icon aria-hidden="true" className={cn('size-3.5', text)} />
              {t(`history.spanKind.${kind}`)}
            </li>
          );
        })}
        <li className="text-muted-foreground flex items-center gap-1.5">
          <span className="bg-error/80 h-2.5 w-4 rounded-sm" aria-hidden="true" />
          {t('history.spanStatus.failed')}
        </li>
        <li className="text-muted-foreground flex items-center gap-1.5">
          <span className="bg-info/60 h-2.5 w-4 rounded-sm" aria-hidden="true" />
          {t('history.spanStatus.running')}
        </li>
      </ul>

      {/* Axis */}
      <div className="grid grid-cols-[minmax(140px,260px)_1fr] border-b">
        <div />
        <div className="text-muted-foreground relative mx-4 h-6 text-[10px] tabular-nums">
          {Array.from({ length: TICKS + 1 }, (_, i) => (
            <span
              key={i}
              className="absolute top-1 -translate-x-1/2 first:translate-x-0 last:-translate-x-full"
              style={{ left: `${String((i / TICKS) * 100)}%` }}
            >
              {formatPreciseDuration((scale * i) / TICKS)}
            </span>
          ))}
        </div>
      </div>

      <div className="scrollbar-transparent max-h-[440px] overflow-y-auto py-1">
        <Lane
          span={workflow}
          label={`${t('history.timeline.workflow')} · ${workflow.label}`}
          pct={pct}
          onSelect={onSelectEvent}
          emphasise
          testId="workflow-inspector-timeline-workflow-lane"
        />
        {spans.map((span) => (
          <Lane
            key={span.key}
            span={span}
            label={span.label}
            pct={pct}
            onSelect={onSelectEvent}
            testId={`workflow-inspector-timeline-lane-${String(span.key)}`}
          />
        ))}
      </div>
    </div>
  );
}

/** Round icon node sitting on a bar end (or alone, for point events). */
function Node({ icon: Icon, tone, leftPct }: { icon: LucideIcon; tone: string; leftPct: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute top-1/2 z-10 flex size-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2',
        tone,
      )}
      style={{ left: `${String(leftPct)}%` }}
    >
      <Icon className="size-3" />
    </span>
  );
}

/** One row: kind icon + label column, then the bar with start/end icon nodes. */
function Lane({
  span,
  label,
  pct,
  onSelect,
  emphasise = false,
  testId,
}: {
  span: HistorySpan;
  label: string;
  pct: (ms: number) => number;
  onSelect: (eventId: number) => void;
  emphasise?: boolean;
  /** `data-testid` for the lane button. */
  testId: string;
}) {
  const { t } = useTranslation('workflow-inspection');
  const { icon, text } = KIND_STYLE[span.kind];
  const KindIcon = icon;
  const isPoint = span.endMs !== null && span.endMs === span.startMs && span.kind !== 'workflow';
  const end = span.endMs;
  const startPct = pct(span.startMs);
  // Open spans stretch to the right edge (latest event).
  const widthPct = end === null ? 100 - startPct : pct(end - span.startMs);
  const durationLabel =
    end === null ? t('history.timeline.inProgress') : formatPreciseDuration(end - span.startMs);
  const title = `${label} — ${t(`history.spanStatus.${span.status}`)}${isPoint ? '' : ` · ${durationLabel}`}${
    span.failureMessage ? `\n${span.failureMessage}` : ''
  }`;
  // Duration rides inside wide bars (timers always — their length is the point).
  const showDurationInBar = !isPoint && (span.kind === 'timer' || widthPct >= 12);

  return (
    <Button
      type="button"
      variant="ghost"
      title={title}
      data-testid={testId}
      onClick={() => {
        const first = span.eventIds[0];
        if (first !== undefined) onSelect(first);
      }}
      className="hover:bg-muted/40 grid h-auto min-h-10 w-full cursor-pointer grid-cols-[minmax(140px,260px)_1fr] items-stretch gap-0 rounded-none px-0 py-0 text-left font-normal whitespace-normal"
    >
      <span
        className={cn(
          'flex items-center gap-1.5 px-3 py-1 text-xs break-words whitespace-normal',
          emphasise ? 'text-foreground font-semibold' : 'text-muted-foreground',
        )}
      >
        <KindIcon aria-hidden="true" className={cn('size-3.5 shrink-0', text)} />
        <span className="min-w-0">{label}</span>
      </span>
      {/* Gridlines every 1/TICKS of the axis, matching the tick labels above. */}
      <span
        className="relative mx-4 block min-h-10"
        style={{
          backgroundImage: 'linear-gradient(to right, var(--border) 1px, transparent 1px)',
          backgroundSize: `${String(100 / TICKS)}% 100%`,
        }}
      >
        {isPoint ? (
          <Node
            icon={KindIcon}
            tone="border-foreground/50 bg-background text-foreground"
            leftPct={startPct}
          />
        ) : (
          <>
            <span
              className={cn(
                'absolute top-1/2 flex h-5 min-w-[4px] -translate-y-1/2 items-center overflow-hidden rounded-full',
                barTone(span),
                emphasise && 'h-6',
              )}
              style={{ left: `${String(startPct)}%`, width: `${String(widthPct)}%` }}
            >
              {showDurationInBar && (
                <span className="text-foreground/80 truncate px-4 text-[10px] font-semibold tabular-nums">
                  {durationLabel}
                </span>
              )}
            </span>
            <Node
              icon={KindIcon}
              tone="border-muted-foreground/40 bg-muted text-muted-foreground"
              leftPct={startPct}
            />
            {end !== null && <Node icon={KindIcon} tone={endNodeTone(span)} leftPct={pct(end)} />}
          </>
        )}
      </span>
    </Button>
  );
}
